/**
 * A tela de configuração — uma só, para todos os grupos.
 *
 * Ela não sabe que opções existem. Cada linha de `config` no banco carrega o
 * próprio `tipo` (qual campo desenhar), `rotulo`, `descricao`, `opcoes`,
 * `minimo` e `maximo` (o que aceitar). Acrescentar uma opção nova é um INSERT
 * na migração, não um deploy de front.
 *
 * A validação real mora no servidor, que é quem lê as mesmas colunas. Os
 * limites aqui são só para o campo não deixar digitar o impossível — se o
 * servidor recusar, o erro aparece embaixo do campo, com o texto dele.
 *
 * O DESENHO (20/10, "tabelas gigantes, ilegíveis… reimaginar tudo"): cada
 * opção é uma LINHA DE AJUSTE — o nome e o que ela muda à esquerda, o controle
 * à direita —, como nas configurações de qualquer sistema. Uma busca no alto
 * acha qualquer opção pelo nome, pela chave ou pela descrição; o histórico é
 * uma linha do tempo; e o que não muda pela tela é uma lista de leitura, sem
 * tabela.
 *
 * AS PEÇAS DO DESIGN SYSTEM (20/10): a busca, as seções, os cartões, as
 * dobras, a linha de ajuste, os campos, o interruptor, os botões e os avisos
 * saem de `src/ds`. Em `gestao.module.css` ficou só o que é desta tela: o
 * valor fixo com o cadeado, os subgrupos do Avançado e a linha do tempo.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ErroApi, type Mudanca, type Opcao } from "../api/cliente";
import { AreaConfig } from "../componentes/AreaConfig";
import {
  AreaDeTexto, Aviso, Botao, Busca, Campo, Cartao, Codigo, Dobra, IconeCadeado, Interruptor,
  LinhaDeAjuste, Secao, Seletor,
} from "../ds";
import { useSessao } from "../sessao";
import css from "./gestao.module.css";

const normal = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export function Configuracao() {
  const { podeOperar } = useSessao();
  const { data, isPending, error } = useQuery({ queryKey: ["config"], queryFn: api.config });
  const [busca, setBusca] = useState("");

  const termo = normal(busca.trim());
  const achadas = useMemo(
    () =>
      !termo || !data
        ? []
        : data.filter((o) =>
            normal(
              [o.painel_titulo, o.rotulo, o.chave, o.descricao, o.painel_explica, o.grupo_rotulo, o.secao]
                .filter(Boolean)
                .join(" "),
            ).includes(termo),
          ),
    [data, termo],
  );

  const buscaCampo = (
    <Busca rotulo="Buscar um ajuste" placeholder="Buscar um ajuste…" value={busca}
           onChange={(e) => setBusca(e.currentTarget.value)} />
  );

  if (isPending || error) {
    return (
      <AreaConfig titulo="Regras e ajustes">
        {isPending ? (
          <Aviso simples>Lendo a configuração…</Aviso>
        ) : (
          <Aviso tom="erro" simples>{(error as Error).message}</Aviso>
        )}
      </AreaConfig>
    );
  }

  // O PAINEL DA OPERAÇÃO (0141, 14/10): o Pedro, "a tela de configurações tá
  // tudo muito bagunçada e inconclusiva" — 211 opções no mesmo nível. Quem diz
  // o que vai para o topo é o servidor (`config_painel`), como as seções da
  // 0050: a tela não sabe quais são. A TRAVADA (mínimo igual ao máximo, ou uma
  // opção só) sai da edição e vai para uma lista só de leitura. O resto fica
  // em "Avançado", fechado. Nenhuma opção some.
  const painel = data.filter((o) => o.painel_bloco);
  const travadas = data.filter((o) => o.travada && !o.painel_bloco);
  const tecnicas = data.filter((o) => !o.painel_bloco && !o.travada);

  const blocosDoPainel = new Map<string, { ordem: number; opcoes: Opcao[] }>();
  for (const o of painel) {
    const b = blocosDoPainel.get(o.painel_bloco!) ?? { ordem: o.painel_bloco_ordem ?? 99, opcoes: [] };
    b.opcoes.push(o);
    blocosDoPainel.set(o.painel_bloco!, b);
  }

  // SEÇÃO ⊃ GRUPO ⊃ CHAVE, preservando a ordem que o servidor mandou. O nível
  // da seção vem da migração 0050 e não de um mapa aqui: grupo não mapeado
  // chega como `secao: "Outros"`, e esquecer de mapear custa um rótulo
  // estranho, nunca uma chave perdida.
  const secoes = new Map<string, { ordem: number; grupos: Map<string, Opcao[]> }>();
  for (const o of tecnicas) {
    const s = secoes.get(o.secao) ?? { ordem: o.secao_ordem, grupos: new Map() };
    const nome = o.grupo_rotulo ?? o.grupo;
    s.grupos.set(nome, [...(s.grupos.get(nome) ?? []), o]);
    secoes.set(o.secao, s);
  }
  const blocosTecnicos = [...secoes]
    .map(([nome, s]) => ({ nome, ordem: s.ordem, grupos: [...s.grupos] }))
    .sort((a, b) => a.ordem - b.ordem);

  return (
    <AreaConfig
      titulo="Regras e ajustes"
      descricao={
        <>
          O que se mexe na operação do dia a dia, com o que cada valor muda na
          prática. Vale na próxima vez que o sistema roda, sem reiniciar nada.
          {!podeOperar && " Você entrou como leitor: pode ver, não pode alterar."}
        </>
      }
      acoes={buscaCampo}
    >
      {termo ? (
        // A BUSCA: as opções que casam, de qualquer bloco, cada uma com o
        // caminho dela. A travada aparece só para leitura.
        <Secao
          rotulo="Resultado da busca"
          nota={
            achadas.length === 0
              ? `Nenhum ajuste com “${busca.trim()}”.`
              : `${achadas.length} ${achadas.length === 1 ? "ajuste" : "ajustes"} com “${busca.trim()}”.`
          }
        >
          {achadas.length > 0 && (
            <Cartao linhas>
              {achadas.map((o) =>
                o.travada && !o.painel_bloco ? (
                  <Travada key={o.chave} opcao={o} caminho />
                ) : (
                  <Ajuste key={o.chave} opcao={o} bloqueado={!podeOperar} caminho
                          titulo={o.painel_titulo} explica={o.painel_explica} />
                ),
              )}
            </Cartao>
          )}
        </Secao>
      ) : (
        <>
          <Secao titulo="O dia a dia">
            {[...blocosDoPainel]
              .sort((a, b) => a[1].ordem - b[1].ordem)
              .map(([nome, b]) => (
                <Cartao key={nome} titulo={nome} linhas>
                  {[...b.opcoes]
                    .sort((x, y) => (x.painel_ordem ?? 99) - (y.painel_ordem ?? 99))
                    .map((o) => (
                      <Ajuste key={o.chave} opcao={o} bloqueado={!podeOperar}
                              titulo={o.painel_titulo} explica={o.painel_explica} />
                    ))}
                </Cartao>
              ))}
          </Secao>

          <Secao
            titulo="Ajustes técnicos"
            conta={tecnicas.length}
            nota={
              <>
                Pisos, pesos, tetos, tempos e versões de cada etapa. Os valores de hoje
                foram medidos e estão registrados no adendo do documento de regras:
                mexa só sabendo o que a opção faz.
              </>
            }
          >
            {blocosTecnicos.map((b) => (
              <Dobra key={b.nome} titulo={b.nome} solto
                     resumo={`${b.grupos.reduce((n, [, ops]) => n + ops.length, 0)} opções`}>
                {b.grupos.map(([rotulo, opcoes]) => (
                  <div key={rotulo} className={css.dobraGrupo}>
                    {b.grupos.length > 1 && <p className={css.dobraGrupoTitulo}>{rotulo}</p>}
                    <div className={css.dobraGrupoCorpo}>
                      {opcoes.map((o) => (
                        <Ajuste key={o.chave} opcao={o} bloqueado={!podeOperar} />
                      ))}
                    </div>
                  </div>
                ))}
              </Dobra>
            ))}
          </Secao>

          <Dobra titulo="Fixados pelas regras da Nectar" linhas
                 resumo={`${travadas.length} valores que não mudam pela tela`}>
            {travadas.map((o) => (
              <Travada key={o.chave} opcao={o} />
            ))}
          </Dobra>

          <Historico />
        </>
      )}
    </AreaConfig>
  );
}

/**
 * O que não muda pela tela, só para ler: os números do documento de regras
 * (0091: mínimo igual ao máximo) e o modelo e o esforço da IA de cada etapa
 * (D9, 0105: uma opção só). O servidor recusaria qualquer outro valor com 422.
 */
function Travada({ opcao: o, caminho }: { opcao: Opcao; caminho?: boolean }) {
  return (
    <LinhaDeAjuste
      nome={<>{o.rotulo} <Codigo>{o.chave}</Codigo></>}
      explica={
        <>
          {caminho && <span className={css.mudo}>{o.secao} · </span>}
          {o.tipo === "opcao"
            ? "Modelo e esforço da IA travados (D9) — mudar é migração."
            : "Número do documento de regras — mudar é mudar o documento."}
        </>
      }
    >
      <span className={css.valorFixo}>
        <IconeCadeado tamanho={13} />
        {paraTexto(o) || "—"}
      </span>
    </LinhaDeAjuste>
  );
}

/**
 * Quem mudou o quê, numa linha do tempo.
 *
 * Nenhuma rota grava isto: é um gatilho no Postgres, disparado por qualquer
 * escrita em `config`, `termo` ou `categoria`. Por isso não há como uma
 * alteração escapar do registro — nem a feita por script, que entra como
 * `sistema`.
 */
function Historico() {
  const { data } = useQuery({
    queryKey: ["historico"],
    queryFn: () => api.historico(30),
  });
  if (!data?.length) return null;

  return (
    <Secao titulo="Histórico de alterações" conta={data.length}>
      <ol className={css.tempo}>
        {data.map((m, i) => (
          <li key={`${m.quando}-${m.chave}-${i}`}>
            <span className={css.tempoQuem} aria-hidden="true">
              {(m.quem || "?").slice(0, 1).toUpperCase()}
            </span>
            <div className={css.tempoCorpo}>
              <p className={css.tempoTitulo}>
                <b>{m.quem}</b> {verbo(m)} <Codigo>{m.chave}</Codigo>
                <span className={css.mudo}> · {m.tabela} · {quando(m.quando)}</span>
              </p>
              <p className={css.tempoMudanca} title={mudanca(m)}>
                {mudanca(m)}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </Secao>
  );
}

const verbo = (m: Mudanca) =>
  m.acao === "criou" ? "criou" : m.acao === "apagou" ? "excluiu" : "alterou";

const mudanca = (m: Mudanca) =>
  m.acao === "criou"
    ? resumo(m.valor_depois)
    : m.acao === "apagou"
      ? resumo(m.valor_antes)
      : `${resumo(m.valor_antes)} → ${resumo(m.valor_depois)}`;

const resumo = (v: unknown) => {
  const t = typeof v === "string" ? v : JSON.stringify(v ?? "—");
  return t.length > 140 ? `${t.slice(0, 140)}…` : t;
};

/** Uma opção como linha de ajuste: o que é à esquerda, o controle à direita. */
function Ajuste({ opcao, bloqueado, titulo, explica, caminho }: {
  opcao: Opcao;
  bloqueado: boolean;
  /** No painel da operação (0141): o título e a frase do que muda na prática. */
  titulo?: string | null;
  explica?: string | null;
  /** Na busca: diz de onde a opção vem. */
  caminho?: boolean;
}) {
  const cliente = useQueryClient();
  const [rascunho, setRascunho] = useState(() => paraTexto(opcao));

  const salvar = useMutation({
    mutationFn: (valor: unknown) => api.mudaConfig(opcao.chave, valor),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ["config"] });
      // O funil e a fila leem esta configuração. Sem isto a tela do lado
      // continuaria mostrando o corte antigo até alguém recarregar.
      void cliente.invalidateQueries({ queryKey: ["funil"] });
      void cliente.invalidateQueries({ queryKey: ["fila"] });
      void cliente.invalidateQueries({ queryKey: ["historico"] });
    },
  });

  const original = paraTexto(opcao);
  const mudou = rascunho !== original;
  const id = `cfg-${opcao.chave}`;
  const largo = opcao.tipo === "texto_longo" || opcao.tipo === "lista_texto";
  const explicacao = explica || opcao.descricao;

  return (
    <LinhaDeAjuste
      para={id}
      largo={largo}
      nome={
        <>
          {titulo || opcao.rotulo}
          {!titulo && <Codigo>{opcao.chave}</Codigo>}
        </>
      }
      explica={
        explicacao && (
          <>
            {caminho && (
              <span className={css.mudo}>
                {opcao.painel_bloco ? `Operação · ${opcao.painel_bloco}` : opcao.secao} ·{" "}
              </span>
            )}
            {explicacao}
          </>
        )
      }
      mais={explica && opcao.descricao && <>{opcao.descricao} <Codigo>{opcao.chave}</Codigo></>}
      rodape={
        opcao.atualizado_por && (
          <>
            Alterado por {opcao.atualizado_por}
            {opcao.atualizado_em && ` em ${quando(opcao.atualizado_em)}`}
          </>
        )
      }
      erro={salvar.error instanceof ErroApi && <Aviso tom="erro" simples>{salvar.error.message}</Aviso>}
    >
      <Entrada
        id={id}
        opcao={opcao}
        valor={rascunho}
        aoMudar={setRascunho}
        bloqueado={bloqueado || salvar.isPending}
      />
      {mudou && !bloqueado && (
        <>
          <Botao variante="primario"
                 onClick={() => salvar.mutate(deTexto(rascunho, opcao.tipo))}
                 disabled={salvar.isPending}>
            {salvar.isPending ? "Salvando…" : "Salvar"}
          </Botao>
          <Botao onClick={() => setRascunho(original)}>Desfazer</Botao>
        </>
      )}
    </LinhaDeAjuste>
  );
}

/** O campo certo para o tipo que a linha declara. */
function Entrada({ id, opcao, valor, aoMudar, bloqueado }: {
  id: string;
  opcao: Opcao;
  valor: string;
  aoMudar: (v: string) => void;
  bloqueado: boolean;
}) {
  // Mínimo igual ao máximo é número FIXADO, não calibragem: é assim que a
  // migração 0091 trava os números do documento de regras (50, 70 e
  // R$ 250 mil). A OPÇÃO ÚNICA é a mesma trava para texto (0105, D9). O campo
  // desligado diz isso antes de a pessoa tentar.
  const fixo = ((opcao.tipo === "inteiro" || opcao.tipo === "decimal")
    && opcao.minimo != null && opcao.minimo === opcao.maximo)
    || (opcao.tipo === "opcao" && (opcao.opcoes ?? []).length === 1);
  const comum = { id, disabled: bloqueado || fixo };

  switch (opcao.tipo) {
    case "booleano":
      return (
        <Interruptor {...comum} checked={valor === "true"}
                     onChange={(e) => aoMudar(String(e.target.checked))} />
      );

    case "opcao":
      return (
        <Seletor {...comum} valor={valor} aoMudar={aoMudar}
                 opcoes={(opcao.opcoes ?? []).map((o) => ({ valor: o, rotulo: o }))} />
      );

    case "inteiro":
    case "decimal":
      return (
        <Campo {...comum} type="number" value={valor}
               step={opcao.tipo === "inteiro" ? 1 : "any"}
               min={opcao.minimo ?? undefined} max={opcao.maximo ?? undefined}
               onChange={(e) => aoMudar(e.target.value)} />
      );

    case "texto_longo":
      return (
        <AreaDeTexto {...comum} rows={10} value={valor}
                     onChange={(e) => aoMudar(e.target.value)} />
      );

    case "lista_texto":
      return (
        <AreaDeTexto {...comum} rows={5} value={valor} spellCheck={false}
                     onChange={(e) => aoMudar(e.target.value)} />
      );

    default:
      return (
        <Campo {...comum} type="text" value={valor}
               onChange={(e) => aoMudar(e.target.value)} />
      );
  }
}

/* ── conversão ────────────────────────────────────────────────────────────
   O valor trafega como JSON e é editado como texto. Uma lista vira uma linha
   por item porque é assim que se lê e se cola uma lista de situações; vírgula
   não serve, "Revogada, por decisão judicial" é um item só. */

function paraTexto(o: Opcao): string {
  if (o.valor === null || o.valor === undefined) return "";
  if (o.tipo === "lista_texto") return (o.valor as string[]).join("\n");
  if (o.tipo === "booleano") return String(o.valor);
  return typeof o.valor === "string" ? o.valor : JSON.stringify(o.valor);
}

function deTexto(texto: string, tipo: Opcao["tipo"]): unknown {
  switch (tipo) {
    case "booleano":
      return texto === "true";
    case "inteiro":
      return parseInt(texto, 10);
    case "decimal":
      return parseFloat(texto);
    case "lista_texto":
      return texto.split("\n").map((l) => l.trim()).filter(Boolean);
    default:
      return texto;
  }
}

function quando(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}
